import {
  Component,
  OnInit,
  Input,
  ComponentFactoryResolver,
  ViewChild,
  ChangeDetectorRef,
  Inject,
  forwardRef,
  OnDestroy,
  AfterViewInit
} from '@angular/core';
import { CalcOnChangeDataEntityComponent } from '../../data-entity-input/calc-on-change-data-entity';
import { IssueDateDataEntity } from 'src/app/models/data-entities/issue-date-data-entity';
import { UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { NgbDateParserFormatter, NgbInputDatepicker, NgbDateStruct } from '@ng-bootstrap/ng-bootstrap';
import { NgbDateCustomParserFormatter } from 'src/app/models/dateformat';
import moment from 'moment';
import {
  WorkflowService,
  WorkflowContextService,
  SecurityService
} from 'src/app/services';
import { LayoutService } from 'src/app/services/layout.service';
import { Subscription } from 'rxjs';

@Component({
  selector: 'wm-issue-date-data-entity-input',
  templateUrl: './issue-date-data-entity-input.component.html',
  styleUrls: ['./issue-date-data-entity-input.component.css'],
  providers: [
    { provide: NgbDateParserFormatter, useClass: NgbDateCustomParserFormatter }
  ],
  standalone: false
})
export class IssueDateDataEntityInputComponent extends CalcOnChangeDataEntityComponent
  implements OnInit, OnDestroy, AfterViewInit {
  @Input() entity: IssueDateDataEntity;
  @ViewChild('d', { static: false }) picker: NgbInputDatepicker;

  dateForm: UntypedFormGroup;
  date: NgbDateStruct;

  private _datepickerSub: Subscription;

  constructor(
    public layoutService: LayoutService,
    componentFactoryResolver: ComponentFactoryResolver,
    ref: ChangeDetectorRef,
    @Inject(forwardRef(() => WorkflowService)) workflowSvc: WorkflowService,
    @Inject(forwardRef(() => WorkflowContextService)) context: WorkflowContextService,
    @Inject(forwardRef(() => SecurityService)) securitySvc: SecurityService
  ) {
    super(layoutService, componentFactoryResolver, workflowSvc, context, securitySvc, ref);
    layoutService.useNewLayout$.subscribe(v => (this.templateForPermitForm = v));
  }

  override ngAfterViewInit(): void {
    super.ngAfterViewInit();
  }

  override ngOnDestroy(): void {
    this._datepickerSub?.unsubscribe();
    super.ngOnDestroy();
    if (this.form && this.dateForm) {
      this.form.removeControl('dateControl');
    }
  }

  override async ngOnInit() {
    // Initialises calculatingFormControlName, valueObj, modelValue and entity.editedValue,
    // and calls watchForCalculation() when same-activity dependencies exist.
    await super.ngOnInit();

    // Seed the date picker from whatever the server returned.
    this._applyDateValue(this.entity.getEffectiveValue() || this.modelValue);

    this.dateForm = new UntypedFormGroup({
      [`${this.entity.templateCode}-DATE`]: new UntypedFormControl(
        this.date,
        this.entity.isRequired ? [Validators.required] : []
      )
    });

    if (this.form) {
      this.form.addControl('dateControl', this.dateForm);
    }

    if (this.templateForPermitForm) {
      const v = this.entity.editedValue || this.entity.initialValue;
      this.form.controls[`${this.entity.templateCode}`]?.setValue(v);
      this.dateForm.controls[`${this.entity.templateCode}-DATE`]?.setValue(v);
    }

    // Keep the date picker and read-only display in sync when the formula recalculates.
    // The base class updates formulaValue/modelValue but not formattedValue, which is
    // what entity.initialValue (and the read-only template binding) reads from.
    this._datepickerSub = this.entity.valueUpdate$.subscribe(value => {
      if (value) {
        this.entity.formattedValue = value;
        this._applyDateValue(value);
        this.dateForm?.controls[`${this.entity.templateCode}-DATE`]?.setValue(this.date);
      }
    });
  }

  getSafeDisplayDate(value: string): string {
  if (!value) return '';

  try {
    const m = moment(
      value,
      ['MM/DD/YYYY', 'YYYY-MM-DD', moment.ISO_8601],
      true
    );

    return m.isValid() && m.year() > 1
      ? m.format('M/D/YYYY')
      : '';
  } catch {
    return '';
  }
}

  private _applyDateValue(value: string): void {
    if (!value) return;
    const m = moment(value, ['MM/DD/YYYY', 'YYYY-MM-DD', moment.ISO_8601]);
    if (m.isValid() && m.year() > 1) {
      this.date = { year: m.year(), month: m.month() + 1, day: m.date() };
    }
  }

  getDateValue(value: string): Date | null {
    if (!value) return null;
    const m = moment(value, ['MM/DD/YYYY', 'YYYY-MM-DD', moment.ISO_8601]);
    return m.isValid() && m.year() > 1 ? m.toDate() : null;
  }

  onCommonTextfieldDateChange(isoString: string): void {
    if (!isoString || isoString === 'Invalid Date') {
      this.entity.editedValue = null;
      return;
    }
    const formatted = moment(isoString).format('MM/DD/YYYY');
    this.entity.editedValue = formatted !== this.entity.initialValue ? formatted : null;
    this.entity.value = this.entity.getEffectiveValue();
    this.form.controls[`${this.entity.templateCode}`].setValue(this.entity.value);
  }

  onDateSelect() {
    const dateValue = this.dateForm.controls[`${this.entity.templateCode}-DATE`].value;
    this.date = dateValue;

    if (dateValue) {
      const formattedDate = moment({
        year: dateValue.year,
        month: dateValue.month - 1,
        day: dateValue.day
      }).format('MM/DD/YYYY');

      this.entity.editedValue = formattedDate !== this.entity.initialValue ? formattedDate : null;
      this.entity.value = this.entity.getEffectiveValue();
      this.form.controls[`${this.entity.templateCode}`].setValue(this.entity.value);
    } else {
      this.entity.editedValue = null;
      this.entity.value = this.entity.initialValue;
    }
  }

  onFocus() {
    this.picker.open();
    return true;
  }
}

